import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Home, Briefcase, Navigation, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';

const LocationPanel = ({ onClose, onLocationUpdate, currentLocations }) => {
  const [activeTab, setActiveTab] = useState('home');
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGetCurrentLocation = () => {
    setLoading(true);

    if (!navigator.geolocation) {
      toast.error('Twoja przeglądarka nie wspiera geolokalizacji');
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          // Reverse geocoding (can integrate with OpenStreetMap Nominatim)
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          );
          const data = await response.json();

          console.log('Nominatim reverse geocoding response:', data);

          const location = {
            lat: latitude,
            lon: longitude,
            address: data.display_name || 'Nieznany adres',
            city: data.address?.city || data.address?.town || data.address?.village || data.address?.municipality || null,
            country: data.address?.country || null
          };

          console.log('Parsed location:', location);

          // Use activeTab (home or work) instead of 'current'
          onLocationUpdate(activeTab, location);
          toast.success(`Lokalizacja zapisana: ${activeTab === 'home' ? 'Dom' : 'Praca'}`);
          setAddress('');
        } catch (error) {
          toast.error('Nie udało się pobrać adresu');
          console.error('Geocoding error:', error);
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        toast.error('Nie udało się pobrać lokalizacji');
        console.error(error);
        setLoading(false);
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!address.trim()) {
      toast.error('Wpisz adres');
      return;
    }

    setLoading(true);

    try {
      // Geocoding - convert address to coordinates
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(address)}&format=json&limit=1&addressdetails=1`
      );
      const data = await response.json();

      console.log('Nominatim search response:', data);

      if (data.length === 0) {
        toast.error('Nie znaleziono adresu');
        setLoading(false);
        return;
      }

      const location = {
        lat: parseFloat(data[0].lat),
        lon: parseFloat(data[0].lon),
        address: data[0].display_name,
        city: data[0].address?.city || data[0].address?.town || data[0].address?.village || data[0].address?.municipality || null,
        country: data[0].address?.country || null
      };

      console.log('Parsed location from search:', location);

      onLocationUpdate(activeTab, location);
      toast.success(`Zapisano lokalizację: ${activeTab === 'home' ? 'Dom' : 'Praca'}`);
      setAddress('');
    } catch (error) {
      toast.error('Błąd podczas wyszukiwania adresu');
      console.error('Geocoding search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 50 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: { duration: 0.2 }
    }
  };

  return (
    <motion.div
      className="location-modal-backdrop"
      variants={backdropVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      onClick={onClose}
    >
      <motion.div
        className="location-modal"
        variants={modalVariants}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>📍 Twoja Lokalizacja</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="tabs">
          <button
            className={`tab ${activeTab === 'home' ? 'active' : ''}`}
            onClick={() => setActiveTab('home')}
          >
            <Home size={20} />
            <span>Dom</span>
          </button>
          <button
            className={`tab ${activeTab === 'work' ? 'active' : ''}`}
            onClick={() => setActiveTab('work')}
          >
            <Briefcase size={20} />
            <span>Praca</span>
          </button>
        </div>

        <div className="modal-content">
          {currentLocations[activeTab] && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="current-location-display"
            >
              <MapPin size={16} />
              <span>{currentLocations[activeTab].address}</span>
            </motion.div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>
                {activeTab === 'home' ? 'Gdzie mieszkasz?' : 'Gdzie pracujesz?'}
              </label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="np. ul. Marszałkowska 1, Warszawa"
                className="address-input"
                disabled={loading}
              />
            </div>

            <div className="button-group">
              <motion.button
                type="button"
                className="current-location-btn"
                onClick={handleGetCurrentLocation}
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Navigation size={18} />
                <span>Użyj obecnej lokalizacji</span>
              </motion.button>

              <motion.button
                type="submit"
                className="submit-btn"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {loading ? 'Wyszukiwanie...' : 'Zapisz lokalizację'}
              </motion.button>
            </div>
          </form>

          <div className="help-text">
            <p>💡 Podaj swój adres domowy i pracy, a pomogę Ci znaleźć siłownie:</p>
            <ul>
              <li>W pobliżu Twojego domu</li>
              <li>Po drodze do pracy</li>
              <li>Blisko ważnych dla Ciebie miejsc</li>
            </ul>
          </div>
        </div>
      </motion.div>

      <style jsx>{`
        .location-modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 2rem;
        }

        .location-modal {
          background: white;
          border-radius: 24px;
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 2rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h2 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
        }

        .close-btn {
          background: #f3f4f6;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .close-btn:hover {
          background: #e5e7eb;
          transform: rotate(90deg);
        }

        .tabs {
          display: flex;
          gap: 0.5rem;
          padding: 0 2rem;
          margin-top: 1rem;
        }

        .tab {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: #f3f4f6;
          border: none;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          color: #6b7280;
        }

        .tab.active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .modal-content {
          padding: 2rem;
        }

        .current-location-display {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem;
          background: #f0fdf4;
          border: 1px solid #86efac;
          border-radius: 12px;
          margin-bottom: 1.5rem;
          color: #166534;
          font-size: 0.875rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.5rem;
        }

        .address-input {
          width: 100%;
          padding: 1rem;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          font-size: 1rem;
          transition: all 0.2s;
        }

        .address-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .button-group {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .current-location-btn,
        .submit-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 1rem;
          border: none;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .current-location-btn {
          background: #f3f4f6;
          color: #374151;
        }

        .current-location-btn:hover {
          background: #e5e7eb;
        }

        .submit-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .submit-btn:disabled,
        .current-location-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .help-text {
          background: #f9fafb;
          padding: 1.5rem;
          border-radius: 12px;
          border-left: 4px solid #667eea;
        }

        .help-text p {
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.75rem;
        }

        .help-text ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .help-text li {
          padding: 0.5rem 0;
          color: #6b7280;
          padding-left: 1.5rem;
          position: relative;
        }

        .help-text li::before {
          content: '✓';
          position: absolute;
          left: 0;
          color: #667eea;
          font-weight: bold;
        }
      `}</style>
    </motion.div>
  );
};

export default LocationPanel;
